import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link } from "react-router";
import {
  Activity,
  AlertTriangle,
  Download,
  FileText,
  PlusCircle,
  Slash,
  Upload,
  XCircle,
} from "react-feather";
import { apiErrorMessage, useDelete, useGetLazy, usePost } from "../../api";
import { usePermissions } from "../../hooks";
import {
  COUPON_BULK_CREATE_COLUMNS,
  CouponBulkUpdateItemStatus,
  CouponBulkUpdateOperation,
  ICouponBulkUpdateFileResponse,
  ICouponBulkUpdateItemsResponse,
  ICouponBulkUpdateRunSummary,
  ICouponBulkUpdateRunsResponse,
  ICouponBulkUpdateStageResponse,
  KindredPublishStatus,
  PermissionAction,
  PermissionResource,
} from "../../interfaces";
import {
  EmptyState,
  FilterBar,
  FilterField,
  PageHeader,
  PageToolbar,
  ToolbarButton,
} from "../../components/ui";

const BASE = "coupon-bulk-updates";
const PAGE_SIZE = 25;
const ITEMS_PAGE_SIZE = 100;
const POLL_MS = 5000;

const OPERATION_LABEL: Record<CouponBulkUpdateOperation, string> = {
  [CouponBulkUpdateOperation.Coverage]: "Update coverage",
  [CouponBulkUpdateOperation.Deactivate]: "Deactivate",
  [CouponBulkUpdateOperation.Create]: "Create coupons",
};

const RUN_STATUS_VARIANT: Record<ICouponBulkUpdateRunSummary["status"], string> =
  {
    staged: "secondary",
    pending: "info",
    running: "primary",
    completed: "success",
    failed: "danger",
    cancelled: "warning",
  };

const ITEM_STATUS: Record<
  CouponBulkUpdateItemStatus,
  { label: string; variant: string }
> = {
  pending: { label: "Queued", variant: "primary" },
  updated: { label: "Updated", variant: "success" },
  created: { label: "Created", variant: "success" },
  failed: { label: "Failed", variant: "danger" },
  cancelled: { label: "Cancelled", variant: "secondary" },
  no_change: { label: "No change", variant: "light" },
  not_found: { label: "Not found", variant: "danger" },
  invalid: { label: "Invalid", variant: "danger" },
  duplicate: { label: "Duplicate", variant: "light" },
};

const PUBLISH_STATUS_LABEL: Record<number, string> = {
  [KindredPublishStatus.Draft]: "Draft",
  [KindredPublishStatus.AwaitingApproval]: "Awaiting approval",
  [KindredPublishStatus.Approved]: "Approved",
  [KindredPublishStatus.Deactivated]: "Deactivated",
};

const describeCoverage = (coverage: number | null) => {
  if (coverage === null) return "—";
  if (coverage === 0) return "None";
  const names: string[] = [];
  if (coverage & 1) names.push("Sitewide");
  if (coverage & 2) names.push("Category");
  if (coverage & 4) names.push("Product");
  if (coverage & 8) names.push("Manufacturer");
  return names.join(" + ");
};

const fmtDuration = (ms: number) => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
};

// Only queued rows count: a create preview already marks codes that exist as no_change.
const processed = (r: ICouponBulkUpdateRunSummary) =>
  r.queuedCount - r.counts.pending - r.counts.cancelled;

// Matches the backend's retry set: rows that were not applied or already correct.
const retryable = (r: ICouponBulkUpdateRunSummary) =>
  r.counts.failed + r.counts.notFound + r.counts.cancelled + r.counts.invalid;

// ── Page ─────────────────────────────────────────────────────────────────────

export const CouponBulkUpdates: React.FC = () => {
  const { can, isAdmin } = usePermissions();
  const canRun =
    isAdmin ||
    can(PermissionAction.CreateAny, PermissionResource.CouponBulkUpdate);

  const [status, setStatus] = useState("");
  const [operation, setOperation] = useState("");
  const [triggeredBy, setTriggeredBy] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ICouponBulkUpdateRunsResponse | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [detailRunId, setDetailRunId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const { execute: fetchRuns, loading, error } =
    useGetLazy<ICouponBulkUpdateRunsResponse>();
  const { execute: cancelApi } = useDelete<unknown>();

  const load = useCallback(
    async (p = page) => {
      const params = new URLSearchParams({
        page: String(p),
        pageSize: String(PAGE_SIZE),
      });
      if (status) params.set("status", status);
      if (operation) params.set("operation", operation);
      if (triggeredBy) params.set("triggeredBy", triggeredBy);
      const r = await fetchRuns(`${BASE}/runs?${params}`).catch(() => null);
      if (r) setData(r);
    },
    [fetchRuns, page, status, operation, triggeredBy]
  );

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasActive = (data?.activeRuns.length ?? 0) > 0;
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => load(), POLL_MS);
    return () => clearInterval(t);
  }, [hasActive, load]);

  const applyFilters = () => {
    setPage(1);
    load(1);
  };
  const clearFilters = () => {
    setStatus("");
    setOperation("");
    setTriggeredBy("");
    setPage(1);
    fetchRuns(`${BASE}/runs?page=1&pageSize=${PAGE_SIZE}`)
      .then(setData)
      .catch(() => null);
  };

  const cancelRun = async (run: ICouponBulkUpdateRunSummary) => {
    if (
      !window.confirm(
        `Stop this ${OPERATION_LABEL[run.operation].toLowerCase()} run? Rows already written stay written; the rest are marked cancelled and can be downloaded to retry.`
      )
    )
      return;
    setCancellingId(run.runId);
    try {
      await cancelApi(`${BASE}/runs/${run.runId}`);
      setNotice("Run cancelled.");
      await load();
    } catch (err) {
      setNotice(apiErrorMessage(err));
    } finally {
      setCancellingId(null);
    }
  };

  const totalPages = Math.max(
    1,
    Math.ceil((data?.total ?? 0) / (data?.pageSize ?? PAGE_SIZE))
  );
  const activeFilters = [status, operation, triggeredBy].filter(Boolean).length;

  return (
    <div>
      <PageHeader
        title="Coupon Bulk Updates"
        description="Change many coupons at once from a CSV. Every file is checked before anything is written, runs in the background, and is kept with an audit record."
        actions={
          <Link to="/kindred/coupons" className="small">
            Back to coupons
          </Link>
        }
      />

      <PageToolbar>
        <ToolbarButton
          label="New bulk update"
          hint={
            canRun
              ? "Upload a CSV to update coverage or deactivate coupons. You'll see exactly what will change before confirming."
              : "You don't have permission to run bulk updates."
          }
          icon="bi-upload"
          variant="primary"
          onClick={() => setShowNew(true)}
          disabled={!canRun}
        />
        <ToolbarButton
          label="Refresh"
          hint="Reload the runs list."
          icon="bi-arrow-clockwise"
          onClick={() => load()}
        />
      </PageToolbar>

      {notice && (
        <Alert variant="info" dismissible onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && <Alert variant="danger">{apiErrorMessage(error)}</Alert>}

      <Card className="mb-3 border-primary">
        <Card.Header className="py-2 bg-primary bg-opacity-10 fw-semibold small d-flex align-items-center">
          <Activity size={14} className="me-1 text-primary" />
          Currently running
          {hasActive && (
            <Badge bg="primary" className="ms-2">
              {data!.activeRuns.length}
            </Badge>
          )}
          <span className="ms-auto text-muted fw-normal" style={{ fontSize: 11 }}>
            {hasActive
              ? `Refreshing every ${POLL_MS / 1000}s`
              : "Shown regardless of the filters below"}
          </span>
        </Card.Header>
        <Card.Body className="p-0">
          {!hasActive ? (
            <div className="text-center text-muted small py-3">
              Nothing running right now.
            </div>
          ) : (
            <RunsTable
              runs={data!.activeRuns}
              onDetail={setDetailRunId}
              onCancel={canRun ? cancelRun : undefined}
              cancellingId={cancellingId}
            />
          )}
        </Card.Body>
      </Card>

      <FilterBar
        onSearch={applyFilters}
        onClear={clearFilters}
        activeCount={activeFilters}
        summary={data ? `${data.total} run${data.total === 1 ? "" : "s"}` : undefined}
      >
        <FilterField label="Status" hint="Unconfirmed previews only show when you pick Preview.">
          <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any (excluding previews)</option>
            <option value="running">Running</option>
            <option value="pending">Queued</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="staged">Preview (not confirmed)</option>
          </Form.Select>
        </FilterField>
        <FilterField label="Operation" hint="What the run changed.">
          <Form.Select size="sm" value={operation} onChange={(e) => setOperation(e.target.value)}>
            <option value="">Any</option>
            {Object.values(CouponBulkUpdateOperation).map((op) => (
              <option key={op} value={op}>
                {OPERATION_LABEL[op]}
              </option>
            ))}
          </Form.Select>
        </FilterField>
        <FilterField label="Triggered by" hint="Who confirmed the run.">
          <Form.Select size="sm" value={triggeredBy} onChange={(e) => setTriggeredBy(e.target.value)}>
            <option value="">Anyone</option>
            {(data?.triggeredByOptions ?? []).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Form.Select>
        </FilterField>
      </FilterBar>

      <Card className="mb-2">
        <Card.Body className="p-0">
          {loading && !data ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : !data || data.runs.length === 0 ? (
            <EmptyState
              title="No runs yet"
              message="Runs appear here once a bulk update is confirmed."
            />
          ) : (
            <RunsTable
              runs={data.runs}
              onDetail={setDetailRunId}
              onCancel={canRun ? cancelRun : undefined}
              cancellingId={cancellingId}
            />
          )}
        </Card.Body>
      </Card>

      <Pager
        page={page}
        totalPages={totalPages}
        onPage={(p) => {
          setPage(p);
          load(p);
        }}
      />

      <NewBulkUpdateModal
        show={showNew}
        onHide={() => setShowNew(false)}
        onStarted={(run) => {
          setShowNew(false);
          setNotice(
            `Queued ${run.queuedCount} coupon${run.queuedCount === 1 ? "" : "s"} — ${OPERATION_LABEL[run.operation].toLowerCase()}. Progress shows under Currently running.`
          );
          load(1);
        }}
      />

      <RunDetailModal runId={detailRunId} onHide={() => setDetailRunId(null)} />
    </div>
  );
};

// ── Runs table ───────────────────────────────────────────────────────────────

const RunsTable: React.FC<{
  runs: ICouponBulkUpdateRunSummary[];
  onDetail: (runId: string) => void;
  onCancel?: (run: ICouponBulkUpdateRunSummary) => void;
  cancellingId: string | null;
}> = ({ runs, onDetail, onCancel, cancellingId }) => (
  <Table size="sm" hover responsive className="mb-0 small align-middle">
    <thead className="table-light">
      <tr>
        <th>Started</th>
        <th>Operation</th>
        <th>Status</th>
        <th>Triggered by</th>
        <th>File</th>
        <th style={{ minWidth: 180 }}>Progress</th>
        <th>Reason</th>
        <th>Duration</th>
        <th className="text-end">Actions</th>
      </tr>
    </thead>
    <tbody>
      {runs.map((run) => {
        const active = run.status === "pending" || run.status === "running";
        const done = processed(run);
        return (
          <tr key={run.runId}>
            <td className="text-muted text-nowrap">
              {new Date(run.startedAt ?? run.stagedAt).toLocaleString()}
            </td>
            <td>{OPERATION_LABEL[run.operation]}</td>
            <td>
              <Badge bg={RUN_STATUS_VARIANT[run.status]}>
                {run.status === "staged" ? "preview" : run.status}
              </Badge>
            </td>
            <td>{run.triggeredByName ?? run.triggeredBy}</td>
            <td className="text-truncate" style={{ maxWidth: 220 }} title={run.storedFileName ?? run.originalFileName}>
              {run.storedFileName ?? run.originalFileName}
            </td>
            <td>
              {run.queuedCount > 0 && (
                <ProgressBar style={{ height: 6 }} className="mb-1">
                  <ProgressBar
                    variant="success"
                    now={((run.counts.updated + run.counts.created) / run.queuedCount) * 100}
                  />
                  <ProgressBar
                    variant="secondary"
                    now={
                      ((done - run.counts.updated - run.counts.created - run.counts.notFound - run.counts.failed) /
                        run.queuedCount) *
                      100
                    }
                  />
                  <ProgressBar
                    variant="danger"
                    now={((run.counts.notFound + run.counts.failed) / run.queuedCount) * 100}
                  />
                </ProgressBar>
              )}
              <span>
                {done}/{run.queuedCount}
              </span>
              {run.counts.notFound > 0 && (
                <span className="text-danger ms-1">{run.counts.notFound} not found</span>
              )}
              {run.counts.failed > 0 && (
                <span className="text-danger ms-1">{run.counts.failed} failed</span>
              )}
            </td>
            <td className="text-truncate" style={{ maxWidth: 220 }} title={run.reason ?? ""}>
              {run.reason ?? "—"}
            </td>
            <td className="text-muted text-nowrap">
              {run.completedAt ? fmtDuration(run.durationMs) : active ? "in progress…" : "—"}
            </td>
            <td className="text-end text-nowrap">
              {active && onCancel && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  className="py-0 px-1 me-1"
                  style={{ fontSize: 11 }}
                  title="Stop this run. Rows already written stay written."
                  disabled={cancellingId === run.runId}
                  onClick={() => onCancel(run)}
                >
                  <XCircle size={11} className="me-1" />
                  Cancel
                </Button>
              )}
              <Button
                size="sm"
                variant="outline-secondary"
                className="py-0 px-1"
                style={{ fontSize: 11 }}
                title="See every row, its outcome and the stored file."
                onClick={() => onDetail(run.runId)}
              >
                Details
              </Button>
            </td>
          </tr>
        );
      })}
    </tbody>
  </Table>
);

const Pager: React.FC<{
  page: number;
  totalPages: number;
  onPage: (p: number) => void;
}> = ({ page, totalPages, onPage }) =>
  totalPages <= 1 ? null : (
    <div className="d-flex justify-content-end align-items-center gap-2 my-2 small">
      <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </Button>
      <span className="text-muted">
        Page {page} of {totalPages}
      </span>
      <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
        Next
      </Button>
    </div>
  );

// ── Row outcomes (used by both the preview and the run detail) ──────────────

const STATUS_FILTERS: { value: string; label: string; count: (r: ICouponBulkUpdateRunSummary) => number }[] = [
  { value: "", label: "All rows", count: (r) => r.counts.total },
  { value: "pending", label: "Queued", count: (r) => r.counts.pending },
  { value: "updated", label: "Updated", count: (r) => r.counts.updated },
  { value: "created", label: "Created", count: (r) => r.counts.created },
  { value: "failed", label: "Failed", count: (r) => r.counts.failed },
  { value: "cancelled", label: "Cancelled", count: (r) => r.counts.cancelled },
  { value: "no_change", label: "No change", count: (r) => r.counts.noChange },
  { value: "not_found", label: "Not found", count: (r) => r.counts.notFound },
  { value: "invalid", label: "Invalid", count: (r) => r.counts.invalid },
  { value: "duplicate", label: "Duplicate", count: (r) => r.counts.duplicate },
];

const ItemsTable: React.FC<{ run: ICouponBulkUpdateRunSummary }> = ({ run }) => {
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const { execute, data, loading } = useGetLazy<ICouponBulkUpdateItemsResponse>();

  useEffect(() => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(ITEMS_PAGE_SIZE),
    });
    if (status) params.set("status", status);
    execute(`${BASE}/runs/${run.runId}/items?${params}`).catch(() => null);
    // Re-fetch as the run progresses too.
  }, [execute, run.runId, status, page, run.counts.pending]);

  const isCoverage = run.operation === CouponBulkUpdateOperation.Coverage;
  const isCreate = run.operation === CouponBulkUpdateOperation.Create;
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / ITEMS_PAGE_SIZE));
  const items = data?.items ?? [];

  return (
    <>
      <div className="d-flex flex-wrap gap-1 mb-2">
        {STATUS_FILTERS.filter((f) => f.value === "" || f.count(run) > 0).map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={status === f.value ? "dark" : "outline-secondary"}
            className="py-0"
            onClick={() => {
              setStatus(f.value);
              setPage(1);
            }}
          >
            {f.label} <Badge bg="light" text="dark">{f.count(run)}</Badge>
          </Button>
        ))}
      </div>
      <div style={{ maxHeight: 380, overflowY: "auto" }}>
        {isCreate ? (
          <CreateItemsTable items={items} loading={loading && !data} />
        ) : (
        <Table size="sm" hover className="mb-0 small">
          <thead className="table-light sticky-top">
            <tr>
              <th>Row</th>
              <th>Coupon</th>
              <th>{isCoverage ? "Coverage" : "Status"}</th>
              {isCoverage && <th>Metadata</th>}
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr>
                <td colSpan={5} className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </td>
              </tr>
            ) : (data?.items ?? []).length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted py-3">
                  No rows.
                </td>
              </tr>
            ) : (
              data!.items.map((item) => (
                <tr key={item.rowNumber}>
                  <td className="text-muted">{item.rowNumber}</td>
                  <td>
                    {item.code ? (
                      <Link to={`/kindred/coupons/${item.couponId}`} target="_blank">
                        {item.code}
                      </Link>
                    ) : null}
                    <div className="font-monospace text-muted" style={{ fontSize: 10 }}>
                      {item.couponId || "—"}
                    </div>
                  </td>
                  <td className="text-nowrap">
                    {isCoverage ? (
                      <>
                        {item.processedAt && `${describeCoverage(item.previousCoverage)} → `}
                        <strong>{item.coverage === null ? item.rawValue || "—" : describeCoverage(item.coverage)}</strong>
                      </>
                    ) : item.previousPublishedStatus === null ? (
                      <strong>Deactivated</strong>
                    ) : (
                      <>
                        {PUBLISH_STATUS_LABEL[item.previousPublishedStatus] ?? item.previousPublishedStatus} →{" "}
                        <strong>Deactivated</strong>
                      </>
                    )}
                  </td>
                  {isCoverage && (
                    <td>
                      {item.metadataUpdated ? (
                        <Badge bg="success">updated</Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  )}
                  <td>
                    <Badge
                      bg={ITEM_STATUS[item.status].variant}
                      text={ITEM_STATUS[item.status].variant === "light" ? "dark" : undefined}
                    >
                      {ITEM_STATUS[item.status].label}
                    </Badge>
                    {item.message && <div className="text-muted">{item.message}</div>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        )}
      </div>
      <Pager page={page} totalPages={totalPages} onPage={setPage} />
    </>
  );
};

const OutcomeCell: React.FC<{ status: CouponBulkUpdateItemStatus; message: string | null }> = ({
  status,
  message,
}) => (
  <td>
    <Badge bg={ITEM_STATUS[status].variant} text={ITEM_STATUS[status].variant === "light" ? "dark" : undefined}>
      {ITEM_STATUS[status].label}
    </Badge>
    {message && <div className="text-muted">{message}</div>}
  </td>
);

const fmtDay = (iso: string) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");

const CreateItemsTable: React.FC<{ items: ICouponBulkUpdateItemsResponse["items"]; loading: boolean }> = ({
  items,
  loading,
}) => (
  <Table size="sm" hover className="mb-0 small">
    <thead className="table-light sticky-top">
      <tr>
        <th>Row</th>
        <th>Code</th>
        <th>Merchant</th>
        <th>Brand</th>
        <th>Valid (UTC)</th>
        <th>Will be</th>
        <th>Outcome</th>
      </tr>
    </thead>
    <tbody>
      {loading ? (
        <tr>
          <td colSpan={7} className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </td>
        </tr>
      ) : items.length === 0 ? (
        <tr>
          <td colSpan={7} className="text-center text-muted py-3">
            No rows.
          </td>
        </tr>
      ) : (
        items.map((item) => {
          const c = item.create;
          return (
            <tr key={item.rowNumber}>
              <td className="text-muted">{item.rowNumber}</td>
              <td>
                {item.couponId ? (
                  <Link to={`/kindred/coupons/${item.couponId}`} target="_blank" className="font-monospace">
                    {c?.code || item.code}
                  </Link>
                ) : (
                  <span className="font-monospace">{c?.code || item.code || "—"}</span>
                )}
                {c?.summary && (
                  <div className="text-muted text-truncate" style={{ maxWidth: 220, fontSize: 11 }} title={c.summary}>
                    {c.summary}
                  </div>
                )}
              </td>
              <td className="text-nowrap">
                {c?.domain || c?.raw.Domain || "—"}
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {c?.countries.join(", ") || "—"}
                </div>
              </td>
              <td>
                {c?.brandId ? (
                  <span title={c.brandId}>{c.brandName ?? c.brandId}</span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="text-nowrap" style={{ fontSize: 11 }}>
                {c ? `${fmtDay(c.validFrom)} → ${fmtDay(c.validTo)}` : "—"}
              </td>
              <td style={{ fontSize: 11 }}>
                {c && (
                  <>
                    {PUBLISH_STATUS_LABEL[c.publishedStatus]} · {describeCoverage(c.coverage)}
                    <div className="text-muted">
                      {c.discount}% · {c.source}
                    </div>
                  </>
                )}
              </td>
              <OutcomeCell status={item.status} message={item.message} />
            </tr>
          );
        })
      )}
    </tbody>
  </Table>
);

// ── Run detail ───────────────────────────────────────────────────────────────

const RunDetailModal: React.FC<{ runId: string | null; onHide: () => void }> = ({
  runId,
  onHide,
}) => {
  const { execute, data: run } = useGetLazy<ICouponBulkUpdateRunSummary>();
  const { execute: fetchFile } = useGetLazy<ICouponBulkUpdateFileResponse>();
  const { execute: fetchRetry } = useGetLazy<string>();
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) return;
    execute(`${BASE}/runs/${runId}`).catch(() => null);
  }, [execute, runId]);

  const active = run?.status === "pending" || run?.status === "running";
  useEffect(() => {
    if (!runId || !active) return;
    const t = setInterval(() => execute(`${BASE}/runs/${runId}`).catch(() => null), POLL_MS);
    return () => clearInterval(t);
  }, [execute, runId, active]);

  const download = async () => {
    setFileError(null);
    try {
      const file = await fetchFile(`${BASE}/runs/${runId}/file`);
      if (file) window.open(file.url, "_blank", "noopener");
    } catch (err) {
      setFileError(apiErrorMessage(err));
    }
  };

  const downloadRetry = async () => {
    setFileError(null);
    try {
      const csv = await fetchRetry(`${BASE}/runs/${runId}/retry-file`, {
        responseType: "text",
      });
      if (!csv) return;
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(run?.storedFileName ?? run?.originalFileName ?? "bulk-update").replace(/\.csv$/i, "")}_retry.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setFileError(apiErrorMessage(err));
    }
  };

  return (
    <Modal show={!!runId} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="h6">
          {run ? `${OPERATION_LABEL[run.operation]} — ${run.triggeredByName ?? run.triggeredBy}` : "Run"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!run || run.runId !== runId ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            <Row className="small mb-3 g-2">
              <Col md={6}>
                <div>
                  <span className="text-muted">Status:</span>{" "}
                  <Badge bg={RUN_STATUS_VARIANT[run.status]}>{run.status}</Badge>
                </div>
                <div>
                  <span className="text-muted">Run ID:</span>{" "}
                  <span className="font-monospace">{run.runId}</span>
                </div>
                <div>
                  <span className="text-muted">Uploaded file:</span> {run.originalFileName}
                </div>
                <div>
                  <span className="text-muted">Stored as:</span> {run.storedFileName ?? "—"}
                  {run.storedFileName && (
                    <Button
                      size="sm"
                      variant="link"
                      className="py-0"
                      title="Download the exact file this run used (link valid 15 minutes)."
                      onClick={download}
                    >
                      <Download size={12} className="me-1" />
                      Download
                    </Button>
                  )}
                </div>
                {!active && retryable(run) > 0 && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="mt-1"
                    title="Rows that failed, weren't found, were cancelled or were invalid — in the upload format with an Error column, ready to fix and upload again."
                    onClick={downloadRetry}
                  >
                    <Download size={12} className="me-1" />
                    Download {retryable(run)} row{retryable(run) === 1 ? "" : "s"} to retry
                  </Button>
                )}
                {fileError && <div className="text-danger">{fileError}</div>}
              </Col>
              <Col md={6}>
                <div>
                  <span className="text-muted">Started:</span>{" "}
                  {run.startedAt ? new Date(run.startedAt).toLocaleString() : "not yet"}
                </div>
                <div>
                  <span className="text-muted">Finished:</span>{" "}
                  {run.completedAt ? `${new Date(run.completedAt).toLocaleString()} (${fmtDuration(run.durationMs)})` : "—"}
                </div>
                {run.cancelledBy && (
                  <div>
                    <span className="text-muted">Cancelled by:</span> {run.cancelledBy}
                  </div>
                )}
                {run.operation === CouponBulkUpdateOperation.Coverage && (
                  <div>
                    <span className="text-muted">Metadata rows updated:</span> {run.counts.metadataUpdated}
                  </div>
                )}
              </Col>
              <Col md={12}>
                <div className="text-muted">Audit comment</div>
                <div className="border rounded p-2 bg-light font-monospace" style={{ fontSize: 11 }}>
                  {run.auditComment ?? "—"}
                </div>
              </Col>
              {run.errorMessage && (
                <Col md={12}>
                  <Alert variant="danger" className="mb-0 py-2">
                    {run.errorMessage}
                  </Alert>
                </Col>
              )}
            </Row>
            <ItemsTable run={run} />
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

// ── New bulk update ──────────────────────────────────────────────────────────

const TEMPLATES: Record<CouponBulkUpdateOperation, string> = {
  [CouponBulkUpdateOperation.Coverage]:
    "CouponId,Coverage\n38069F90-D218-4B3B-AC79-5050C16C8379,Sitewide + Category\n",
  [CouponBulkUpdateOperation.Deactivate]:
    "CouponId\n38069F90-D218-4B3B-AC79-5050C16C8379\n",
  // The BOM makes Excel open the file as UTF-8, so accented summaries survive.
  [CouponBulkUpdateOperation.Create]:
    "\uFEFF" +
    [
      COUPON_BULK_CREATE_COLUMNS.map((c) => c.header),
      COUPON_BULK_CREATE_COLUMNS.map((c) => c.example),
    ]
      .map((r) => r.join(","))
      .join("\n") +
    "\n",
};

// Excel on Windows saves CSV as Windows-1252; reading that as UTF-8 turns every accent into "�".
const readCsv = async (file: File): Promise<string> => {
  const bytes = await file.arrayBuffer();
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
};

const downloadTemplate = (op: CouponBulkUpdateOperation) => {
  const url = URL.createObjectURL(new Blob([TEMPLATES[op]], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `coupon-${op}-template.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const OPERATION_OPTIONS = [
  {
    op: CouponBulkUpdateOperation.Create,
    icon: <PlusCircle size={16} className="me-2" />,
    text: "Add coupons that aren't in Kindred yet. Each row's brand is found from its domain, and codes already on the domain or brand are skipped.",
    columns: COUPON_BULK_CREATE_COLUMNS.filter((c) => c.required).map((c) => c.header).join(", ") + ", …",
  },
  {
    op: CouponBulkUpdateOperation.Coverage,
    icon: <FileText size={16} className="me-2" />,
    text: "Set each coupon's coverage. Coupon metadata is updated too where a metadata row already exists.",
    columns: "CouponId, Coverage",
  },
  {
    op: CouponBulkUpdateOperation.Deactivate,
    icon: <Slash size={16} className="me-2" />,
    text: "Deactivate each coupon: status set to Deactivated, deactivated time set to now (UTC), and your reason saved on the coupon.",
    columns: "CouponId",
  },
];

const CreateColumnsGuide: React.FC = () => (
  <Table size="sm" bordered className="small mb-3">
    <thead className="table-light">
      <tr>
        <th>Column</th>
        <th>Needed</th>
        <th>What goes in it</th>
      </tr>
    </thead>
    <tbody>
      {COUPON_BULK_CREATE_COLUMNS.map((c) => (
        <tr key={c.header}>
          <td className="text-nowrap fw-semibold">{c.header}</td>
          <td>{c.required ? "Required" : "Optional"}</td>
          <td className="text-muted">{c.hint}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);

export const NewBulkUpdateModal: React.FC<{
  show: boolean;
  onHide: () => void;
  onStarted: (run: ICouponBulkUpdateRunSummary) => void;
  initialOperation?: CouponBulkUpdateOperation;
  /** Hide the operation picker, e.g. when opened from "Upload new coupons". */
  lockOperation?: boolean;
}> = ({
  show,
  onHide,
  onStarted,
  initialOperation = CouponBulkUpdateOperation.Coverage,
  lockOperation = false,
}) => {
  const [operation, setOperation] = useState<CouponBulkUpdateOperation>(initialOperation);
  useEffect(() => {
    if (show) setOperation(initialOperation);
  }, [show, initialOperation]);
  const [file, setFile] = useState<File | null>(null);
  const [staged, setStaged] = useState<ICouponBulkUpdateStageResponse | null>(null);
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { execute: stageApi, loading: staging } = usePost<ICouponBulkUpdateStageResponse>();
  const { execute: startApi, loading: starting } = usePost<ICouponBulkUpdateRunSummary>();
  const { execute: discardApi } = useDelete<unknown>();

  const reset = () => {
    setFile(null);
    setStaged(null);
    setReason("");
    setConfirmText("");
    setError(null);
  };

  const close = () => {
    // Throw away an unconfirmed preview rather than leave it to expire.
    if (staged) discardApi(`${BASE}/runs/${staged.run.runId}`).catch(() => null);
    reset();
    onHide();
  };

  const check = async () => {
    if (!file) return;
    setError(null);
    try {
      const csvContent = await readCsv(file);
      const res = await stageApi(`${BASE}/stage`, {
        operation,
        fileName: file.name,
        csvContent,
      });
      setStaged(res);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const start = async () => {
    if (!staged) return;
    setError(null);
    try {
      const run = await startApi(`${BASE}/runs/${staged.run.runId}/start`, {
        reason: reason.trim(),
        confirmCount: Number(confirmText),
      });
      reset();
      onStarted(run);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const run = staged?.run;
  const isDeactivate = operation === CouponBulkUpdateOperation.Deactivate;
  const isCreate = operation === CouponBulkUpdateOperation.Create;
  const rejected = run ? run.counts.invalid + run.counts.duplicate : 0;
  const verb = isCreate ? "Create" : isDeactivate ? "Deactivate" : "Update";
  const canStart =
    !!run &&
    run.queuedCount > 0 &&
    reason.trim().length >= 5 &&
    Number(confirmText) === run.queuedCount;

  return (
    <Modal show={show} onHide={close} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h6">
          {lockOperation && isCreate
            ? "Upload new coupons"
            : `New bulk update${run ? ` — ${OPERATION_LABEL[run.operation]}` : ""}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {!run ? (
          <>
            {!lockOperation && (
              <>
                <Form.Label className="small fw-semibold">What should happen?</Form.Label>
                <Row className="g-2 mb-3">
                  {OPERATION_OPTIONS.map((o) => (
                    <Col md={4} key={o.op}>
                      <Card
                        role="button"
                        onClick={() => setOperation(o.op)}
                        className={`h-100 ${operation === o.op ? "border-primary border-2" : ""}`}
                      >
                        <Card.Body className="py-2">
                          <Form.Check
                            type="radio"
                            checked={operation === o.op}
                            onChange={() => setOperation(o.op)}
                            label={
                              <span className="fw-semibold">
                                {o.icon}
                                {OPERATION_LABEL[o.op]}
                              </span>
                            }
                          />
                          <div className="small text-muted">{o.text}</div>
                          <div className="small mt-1">
                            Columns: <code>{o.columns}</code>{" "}
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 ms-1 align-baseline"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadTemplate(o.op);
                              }}
                            >
                              template
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            )}
            {isCreate && (
              <>
                <div className="small text-muted mb-2">
                  Only these columns are read; any other column in the file is ignored and listed in the
                  check. Header case and spacing don't matter. Up to 10,000 rows per file.{" "}
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 align-baseline"
                    onClick={() => downloadTemplate(CouponBulkUpdateOperation.Create)}
                  >
                    Download template
                  </Button>
                </div>
                <CreateColumnsGuide />
              </>
            )}
            {operation === CouponBulkUpdateOperation.Coverage && (
              <div className="small text-muted mb-3">
                Coverage accepts None, Sitewide, Category, Product, Manufacturer, combinations with "+"
                (e.g. <code>Sitewide + Category</code>), or the number 0–15. Up to 10,000 rows per file.
              </div>
            )}
            <Form.Group>
              <Form.Label className="small fw-semibold">CSV file</Form.Label>
              <Form.Control
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
              />
              <Form.Text className="text-muted">
                Nothing is changed at this step — the file's format and values are checked first.
              </Form.Text>
            </Form.Group>
          </>
        ) : (
          <>
            <Row className="g-2 mb-3">
              <SummaryTile label="Rows to run" value={run.queuedCount} variant="primary" />
              <SummaryTile label="Rejected" value={rejected} variant={rejected > 0 ? "danger" : "secondary"} />
              {isCreate && <SummaryTile label="Already exist" value={run.counts.noChange} variant="secondary" />}
              <SummaryTile label="Rows in file" value={run.counts.total} variant="secondary" />
            </Row>
            {isCreate && run.counts.noChange > 0 && (
              <Alert variant="secondary" className="small py-2">
                {run.counts.noChange} code{run.counts.noChange === 1 ? " is" : "s are"} already in Kindred on the
                same domain or brand and will be skipped. Filter by "No change" to see which coupon each one matched.
              </Alert>
            )}
            {(staged?.warnings ?? []).map((w) => (
              <Alert key={w} variant="warning" className="small py-2">
                <AlertTriangle size={14} className="me-1" />
                {w}
              </Alert>
            ))}
            {(staged?.ignoredColumns.length ?? 0) > 0 && (
              <div className="small text-muted mb-2">
                Ignored column{staged!.ignoredColumns.length === 1 ? "" : "s"} (not read):{" "}
                {staged!.ignoredColumns.join(", ")}
              </div>
            )}
            {rejected > 0 && (
              <Alert variant="warning" className="small py-2">
                <AlertTriangle size={14} className="me-1" />
                {rejected} row{rejected === 1 ? "" : "s"} will be ignored (invalid or duplicated). Check them
                below; only the "Queued" rows are sent to the run.
              </Alert>
            )}
            <ItemsTable run={run} />

            {run.queuedCount > 0 && (
              <Card className="mt-3 border-warning">
                <Card.Body>
                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold">
                      {isDeactivate ? "Deactivation reason" : isCreate ? "Where these coupons came from" : "Reason for this change"}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      maxLength={1000}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={
                        isDeactivate
                          ? "e.g. Codes expired per merchant email 08/10"
                          : isCreate
                            ? "e.g. QA found on Reclame Aqui, tested 15/10"
                            : "e.g. QA review of category-only codes"
                      }
                    />
                    <Form.Text className="text-muted">
                      {isDeactivate
                        ? "Saved as the reason on every deactivated coupon, and in the audit record."
                        : "Saved in the audit record."}{" "}
                      The audit comment will read: your name | stored file name | this reason.
                    </Form.Text>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">
                      Type <code>{run.queuedCount}</code> to confirm{" "}
                      {isDeactivate ? "deactivating" : isCreate ? "creating" : "updating"} {run.queuedCount} coupon
                      {run.queuedCount === 1 ? "" : "s"}
                    </Form.Label>
                    <Form.Control
                      style={{ maxWidth: 200 }}
                      inputMode="numeric"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value.trim())}
                    />
                  </Form.Group>
                  <div className="small text-muted mt-2">
                    {isCreate
                      ? "Each code is checked again just before it is created, so one added by someone else in the meantime is skipped, not duplicated. Anything not created can be downloaded as a file to fix and retry."
                      : "Coupon IDs are checked against live data as the run reaches them. Coupons not found or already correct are reported then, and anything not applied can be downloaded as a file to retry."}
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!run ? (
          <>
            <Button variant="secondary" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" disabled={!file || staging} onClick={check}>
              {staging ? <Spinner animation="border" size="sm" /> : <Upload size={14} className="me-1" />}
              Check file
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              title="Discard this preview and pick a different file."
              onClick={() => {
                discardApi(`${BASE}/runs/${run.runId}`).catch(() => null);
                reset();
              }}
            >
              Start over
            </Button>
            <Button variant="secondary" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button
              variant={isDeactivate ? "danger" : "primary"}
              size="sm"
              disabled={!canStart || starting}
              title="Queue the run. It is processed in the background; you can follow it on this page."
              onClick={start}
            >
              {starting && <Spinner animation="border" size="sm" className="me-1" />}
              {verb} {run.queuedCount} coupon
              {run.queuedCount === 1 ? "" : "s"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const SummaryTile: React.FC<{ label: string; value: number; variant: string }> = ({
  label,
  value,
  variant,
}) => (
  <Col xs={6} md={3}>
    <Card className={`border-${variant}`}>
      <Card.Body className="py-2 text-center">
        <div className={`fs-4 fw-semibold text-${variant}`}>{value}</div>
        <div className="small text-muted">{label}</div>
      </Card.Body>
    </Card>
  </Col>
);
